import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Avatar } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { useDebounce } from "../hooks/useDebounce";
import { fetchProducts } from "../store/slices/productsSlice";
import EnhancedTable from "../components/common/EnhancedTable";
import DeleteConfirmDialog from "../components/dialogs/DeleteConfirmDialog";
import ProductViewModal from "../components/dialogs/ProductViewModal";
import StatusChangeDialog from "../components/dialogs/StatusChangeDialog";
import { productAPI, categoryAPI } from "../services/api";
// import SkeletonLoader from "../components/common/SkeletonLoader";
import StatusChip from "../utils/StatusChip";
import ExportImportButtons from "../components/common/ExportImportButtons";

interface ProductMedia {
  id: string;
  productname: string;
  createdby: string;
  modifiedby: string | null;
  sequence: number;
  productId: string;
  orderId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  invoiceId: string | null;
}

interface Group {
  id: string;
  name: string;
  isprice: boolean;
  noofusers: number;
  categoryId: string | null;
  status: boolean;
  createdby: string;
  modifiedby: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
}

interface GroupPrice {
  id: string;
  newprice: number;
  minimumorderquantity: number;
  groupId: string;
  productId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  group: Group;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
}

interface Product {
  id: string;
  name: string;
  productno: number;
  price: number;
  availablequantity: number;
  productstatus: number;
  inInquiry: boolean;
  expecteddays: number | null;
  createdby: string;
  modifiedby: string;
  sellcounter: number;
  categoryId: string;
  userId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
  productbrandId: string | null;
  other: any;
  description: string;
  productdetails: string;
  producttypeId: number | null;
  videourllink: string | null;
  productvariation: any;
  variationconfig: string;
  productunit: string | null;
  productbrochure: string | null;
  masterdetailId: string;
  lastModifiedPriceDate: string;
  categoryName: string;
  pricemode: boolean;
  minimumorderquantity: number;
  category: Category;
  productmedia: ProductMedia[];
  groupprices: GroupPrice[];
}

const ProductsList: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { products, loading, pagination } = useAppSelector(
    (state: any) => state.products
  );

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    availablequantity: "",
    productstatus: "all",
  });
  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false); // <-- add this
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [productToChangeStatus, setProductToChangeStatus] =
    useState<Product | null>(null);

  // Column reordering state
  const [columns, setColumns] = useState([
    {
      id: "name",
      label: "Product Name",
      minWidth: 200,
      sortable: true,
      draggable: true,
      format: (value: string, row: Product) => {
        const imageUrl = row.productmedia?.[0]?.productname
          ? categoryAPI.getMediaDownloadUrl(row.productmedia[0].productname)
          : undefined;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={imageUrl}
              alt={value}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                fontSize: "1rem",
                fontWeight: "bold",
                border: "2px solid",
                borderColor: "primary.light",
              }}
              onError={(e) => {
                console.log("Image load error for:", imageUrl);
                // Just let the avatar show the letter if image fails
              }}
            >
              {value.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "text.secondary" }}
            >
              {value}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "categoryId",
      label: "Category Name",
      minWidth: 150,
      draggable: true,
      format: (value: string, row: Product) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {row.category?.name || row.categoryName || value || "-"}
        </Typography>
      ),
    },
    {
      id: "price",
      label: "Points",
      minWidth: 120,
      draggable: true,
      sortable: true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value} points
        </Typography>
      ),
    },
    {
      id: "availablequantity",
      label: "Available Quantity",
      minWidth: 150,
      align: "center" as const,
      draggable: true,
      sortable: true,
      format: (value: number) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {value}
        </Typography>
      ),
    },
    {
      id: "productstatus",
      label: "Status",
      minWidth: 120,
      align: "center" as const,
      draggable: true,
      format: (value: number) => <StatusChip status={value} />,
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

  // Debounce Product Name column filter
  const debouncedProductName = useDebounce(columnFilters.name, 500);
  // Debounce Points (price) column filter
  // Debounce Available Quantity column filter
  const debouncedAvailableQuantity = useDebounce(
    columnFilters.availablequantity,
    500
  );

  // Handle column reordering
  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    // Optionally save to localStorage for persistence
    localStorage.setItem(
      "productsTableColumnOrder",
      JSON.stringify(newColumns.map((col: any) => col.id))
    );
  };

  // Load saved column order on component mount
  React.useEffect(() => {
    // Only run once on mount
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem("productsTableColumnOrder");
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds
          .map((id: string) => columns.find((col) => col.id === id))
          .filter(Boolean);

        // Add any new columns that weren't in the saved order
        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter(
          (col: any) => !existingIds.includes(col.id)
        );

        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error("Error loading saved column order:", error);
      }
    }
  }, [columns]); // Now safe to include 'columns' in dependency array

  // Load products with current filters and pagination
  const loadProducts = useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
      include: "category,productmedia,groupprices",
    };

    // Product Name filter (debounced)
    if (debouncedProductName.trim()) {
      params.search = debouncedProductName.trim();
    }

    // Available Quantity filter (debounced)
    if (debouncedAvailableQuantity.trim()) {
      params["where[availablequantity]"] = debouncedAvailableQuantity.trim();
    }
    // Status filter
    if (columnFilters.productstatus !== "all") {
      params["where[productstatus]"] =
        columnFilters.productstatus === "active" ? 1 : 0;
    }

    // Add sort parameter if sorting is active
    if (sortField) {
      params.sort = `${sortField} ${sortDirection}`;
    }

    dispatch(fetchProducts(params));
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    debouncedProductName,
    debouncedAvailableQuantity,
    sortField,
    sortDirection,
    columnFilters.productstatus,
  ]);

  // Reload data when pagination, sorting, status, or debounced search changes
  useEffect(() => {
    if (currentPage > 0 && itemsPerPage > 0) {
      loadProducts();
    }
  }, [
    loadProducts,
    currentPage,
    itemsPerPage,
    debouncedProductName,
    debouncedAvailableQuantity,
    sortField,
    sortDirection,
  ]);

  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleAddProduct = () => {
    navigate("/add-product");
  };

  const handleEditProduct = (product: Product) => {
    navigate(`/add-product/${product.id}`);
  };

  const handleViewProduct = (product: Product) => {
    setSelectedProduct(product);
    setViewModalOpen(true);
  };

  const handleDeleteProduct = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    setDeleteLoading(true); // <-- set loading true
    try {
      await productAPI.deleteProduct(productToDelete.id);
      toast.success("Product deleted successfully!");
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      loadProducts();
    } catch (error: any) {
      console.error("Error deleting product:", error);
      let errorMessage = "Failed to delete product. Please try again.";

      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false); // <-- set loading false
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
    setDeleteLoading(false); // <-- reset loading
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedProduct(null);
    navigate("/products", { replace: true,state: {} });
  };

  // const handleStatusChange = (product: Product) => {
  //   setProductToChangeStatus(product);
  //   setStatusChangeDialogOpen(true);
  // };

  const handleCloseStatusChangeDialog = () => {
    setStatusChangeDialogOpen(false);
    setProductToChangeStatus(null);
  };

  const handleStatusChanged = () => {
    // Reload products after status change
    loadProducts();
  };

  const handleExportProducts = async () => {
    // If there are no products loaded, show an informative toast and skip API call
    if (!Array.isArray(products) || products.length === 0) {
      toast.info("There is no product data to export");
      return;
    }

    try {
      const response = await productAPI.exportProducts();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "products_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exported product data successfully");
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.info("No products are available to export");
      } else {
        toast.error("Failed to export products");
      }
    }
  };

  const handleImportProducts = () => {
    /* TODO: implement import functionality */
  };

  useEffect(() => {
    const viewId = location.state?.viewProductId;
    if (
      viewId &&
      Array.isArray(products) &&
      products.length > 0
    ) {
      console.log("Attempting to view product with ID from state:", viewId);
      const prod = products.find((p: Product) => p.id === viewId);
      console.log("Found product:", prod);
      if (prod) {
        setSelectedProduct(prod);
        setViewModalOpen(true);
      }
    }
  }, [location.state, products.length, products]);

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        // removed gradient background
        backgroundColor: "background.default",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: "#fff",
          boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your catalog, update statuses and export data.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          <ExportImportButtons
            onExport={handleExportProducts}
            onImport={handleImportProducts}
            disabledImport
          />
          <Button
            variant="contained"
            color="warning"
            startIcon={<AddIcon />}
            onClick={handleAddProduct}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: "0 4px 12px -2px rgba(0,0,0,0.18)",
              "&:hover": { boxShadow: "0 6px 16px -2px rgba(0,0,0,0.25)" },
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Table Section Wrapper */}
      <Box sx={{ mt: -1 }}>
        <EnhancedTable
          columns={columns}
          data={products}
          loading={loading}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
          onView={handleViewProduct}
          onStatusChange={(product: Product) => {
            setProductToChangeStatus(product);
            setStatusChangeDialogOpen(true);
          }}
          showViewCondition={(row: Product) => true}
          showStatusChangeCondition={(row: Product) => true}
          enableColumnReordering
          onColumnReorder={handleColumnReorder}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          useExternalPagination
          onSort={handleSort}
          useExternalSorting
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          columnFilters={columnFilters}
          onColumnFilterChange={handleColumnFilterChange}
          statusFilterOptions={{
            productstatus: [
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ],
          }}
          showSelection
          enableStickyFilters
          entityName="product"
        />
      </Box>
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading} // <-- pass loading state
        title="Delete Product"
        message="Are you sure you want to delete this product?"
        itemName={productToDelete?.name || ""}
        itemType="product"
      />

      {/* Product View Modal */}
      <ProductViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        product={selectedProduct}
      />

      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={statusChangeDialogOpen}
        onClose={handleCloseStatusChangeDialog}
        product={productToChangeStatus}
        onStatusChanged={handleStatusChanged}
      />
    </Box>
  );
};

export default ProductsList;
